import { ThemeEvents } from '@theme/events';
import { morph } from '@theme/morph';

const KLAVIYO_BIS_ENDPOINT = 'https://a.klaviyo.com/client/back-in-stock-subscriptions/';
const KLAVIYO_SUBSCRIBE_ENDPOINT = 'https://a.klaviyo.com/client/subscriptions/';

/**
 * <back-in-stock>
 * On-brand "Notify me when back in stock" trigger + modal. Klaviyo owns the
 * subscription, restock detection and email; this component only collects the
 * email and POSTs it to the Klaviyo client-side Back in Stock Subscription API.
 *
 * Reactivity (Horizon): listens for `variant:update` on the closest section and
 * self-morphs from the freshly rendered section HTML, so the button visibility
 * and the server-rendered variant id follow variant switches. Interaction is
 * handled through event delegation on the host so it survives morphs.
 */
class BackInStock extends HTMLElement {
  connectedCallback() {
    this.section = this.closest('.shopify-section, dialog');
    this.section?.addEventListener(ThemeEvents.variantUpdate, this.onVariantUpdate);

    this.addEventListener('click', this.onClick);
    this.addEventListener('submit', this.onSubmit);
  }

  disconnectedCallback() {
    this.section?.removeEventListener(ThemeEvents.variantUpdate, this.onVariantUpdate);
  }

  /** @param {import('@theme/events').VariantUpdateEvent} event */
  onVariantUpdate = (event) => {
    const html = event.detail?.data?.html;
    if (!html) return;

    const next = html.querySelector('back-in-stock');
    if (!next) return;

    // A prior submission's state lives on the host (not touched by childrenOnly
    // morph); clear it so the freshly rendered form for the new variant is pristine.
    this.setState('');

    // childrenOnly keeps this element (and its listeners) alive while swapping the
    // inner button/modal + the server-rendered variant id for the new variant.
    morph(this, next, { childrenOnly: true });
  };

  /** @param {MouseEvent} event */
  onClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    if (target.closest('[data-bis-open]')) {
      event.preventDefault();
      this.openDialog();
    } else if (target.closest('[data-bis-close]')) {
      event.preventDefault();
      this.closeDialog();
    }
  };

  get dialog() {
    return /** @type {HTMLDialogElement | null} */ (this.querySelector('[data-bis-dialog]'));
  }

  openDialog() {
    const dialog = this.dialog;
    if (!dialog) return;

    if (typeof dialog.showModal === 'function') {
      dialog.showModal();
    } else {
      dialog.setAttribute('open', '');
    }
    dialog.querySelector('input[type="email"]')?.focus();
  }

  closeDialog() {
    const dialog = this.dialog;
    if (!dialog) return;
    if (typeof dialog.close === 'function' && dialog.open) {
      dialog.close();
    } else {
      dialog.removeAttribute('open');
    }
  }

  /** @param {SubmitEvent} event */
  onSubmit = (event) => {
    const form = /** @type {HTMLFormElement} */ (event.target);
    if (!form.matches('[data-bis-form]')) return;
    event.preventDefault();
    this.subscribe(form);
  };

  /** @param {HTMLFormElement} form */
  async subscribe(form) {
    const companyId = this.dataset.companyId;
    const revision = this.dataset.revision;
    const email = /** @type {HTMLInputElement | null} */ (form.querySelector('input[name="email"]'))?.value?.trim();
    const variantId = /** @type {HTMLInputElement | null} */ (form.querySelector('input[name="variant_id"]'))?.value;
    const consent = /** @type {HTMLInputElement | null} */ (form.querySelector('input[name="consent"]'))?.checked;

    if (!companyId || !email || !variantId) return;
    if (!form.reportValidity()) return;

    this.setState('loading');
    this.hideMessages();

    try {
      const response = await fetch(`${KLAVIYO_BIS_ENDPOINT}?company_id=${encodeURIComponent(companyId)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', revision },
        body: JSON.stringify({
          data: {
            type: 'back-in-stock-subscription',
            attributes: {
              channels: ['EMAIL'],
              profile: { data: { type: 'profile', attributes: { email } } },
            },
            relationships: {
              variant: { data: { type: 'catalog-variant', id: `$shopify:::$default:::${variantId}` } },
            },
          },
        }),
      });

      if (!response.ok) throw new Error(`Klaviyo BIS responded ${response.status}`);

      if (consent) this.subscribeToMarketing(companyId, revision, email);

      this.setState('success');
      this.showMessage('success');
    } catch (error) {
      console.warn('[back-in-stock]', error);
      this.setState('');
      this.showMessage('error');
    }
  }

  /**
   * Optional marketing opt-in when the consent box is enabled and a list id is set.
   * Fire-and-forget: BIS success must not depend on it.
   */
  subscribeToMarketing(companyId, revision, email) {
    const listId = this.dataset.listId;
    if (!listId) return;

    fetch(`${KLAVIYO_SUBSCRIBE_ENDPOINT}?company_id=${encodeURIComponent(companyId)}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', revision },
      body: JSON.stringify({
        data: {
          type: 'subscription',
          attributes: {
            profile: { data: { type: 'profile', attributes: { email } } },
          },
          relationships: { list: { data: { type: 'list', id: listId } } },
        },
      }),
    }).catch((error) => console.warn('[back-in-stock] marketing opt-in', error));
  }

  setState(state) {
    if (state) this.dataset.bisState = state;
    else delete this.dataset.bisState;
  }

  hideMessages() {
    this.querySelectorAll('[data-bis-success], [data-bis-error]').forEach((el) => (el.hidden = true));
  }

  /** @param {'success' | 'error'} type */
  showMessage(type) {
    const el = this.querySelector(type === 'success' ? '[data-bis-success]' : '[data-bis-error]');
    if (el) el.hidden = false;
  }
}

if (!customElements.get('back-in-stock')) {
  customElements.define('back-in-stock', BackInStock);
}
